import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import PageHeader from '../ui/PageHeader';
import TableSkeleton from '../common/TableSkeleton';
import EmptyState from '../common/EmptyState';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { holidayServices } from '../../services/holidayServices';
import { useSnackbar } from '../../utils/useSnackbar';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={<CelebrationRoundedIcon sx={{ fontSize: 30 }} />}
    title="No Holidays Found"
    description="No upcoming or configured holidays match your search query."
  />
);

/** Helper: Format LocalDate (YYYY-MM-DD) → DD/MM/YYYY */
const toDDMMYYYY = (val) => {
  if (!val) return '—';
  const parts = val.split('T')[0].split('-');
  if (parts.length !== 3) return val;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

/** Get Today in YYYY-MM-DD format */
const getTodayISO = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const HolidayManagement = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const userRoles = useMemo(() => userData?.roles ?? [], [userData]);
  const userId = userData?.id;
  const userName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();

  const [holidays, setHolidays] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);

  const pageSizeOptions = [5, 10, 15];

  // Dialog State (Add / Edit)
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const isSearchFirstRender = useRef(true);

  // Debounce search input
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Holidays
  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'id';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const response = await holidayServices.getAllHolidays(
        paginationModel.page,
        paginationModel.pageSize,
        debouncedSearch,
        sortField,
        sortDir
      );
      setHolidays(response?.content || []);
      setRowCount(response?.totalElements || 0);
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
      showSnackbar('Failed to load holidays.', 'error');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel || []);
  };

  // Open Add Dialog
  const handleOpenAdd = (event) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setIsEditing(false);
    setEditingId(null);
    setFormName('');
    setFormDate('');
    setFormDescription('');
    setFormErrors({});
    setOpenDialog(true);
  };

  // Open Edit Dialog
  const handleOpenEdit = (event, row) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setIsEditing(true);
    setEditingId(row.id);
    setFormName(row.name || '');
    setFormDate(row.holidayDate ? row.holidayDate.split('T')[0] : '');
    setFormDescription(row.description || '');
    setFormErrors({});
    setOpenDialog(true);
  };

  // Open Delete Dialog
  const handleOpenDelete = (event, row) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setHolidayToDelete(row);
    setDeleteDialogOpen(true);
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    const trimmedName = formName.trim();
    if (!trimmedName) {
      errors.name = 'Holiday name is required.';
    } else if (trimmedName.length > 100) {
      errors.name = 'Holiday name cannot exceed 100 characters.';
    }

    if (!formDate) {
      errors.date = 'Holiday date is required.';
    } else {
      const todayISO = getTodayISO();
      if (formDate < todayISO) {
        errors.date = 'Holiday date cannot be in the past.';
      }
    }

    if (formDescription && formDescription.trim().length > 500) {
      errors.description = 'Description cannot exceed 500 characters.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save Holiday (Add / Edit)
  const handleSaveHoliday = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const payload = {
        name: formName.trim(),
        holidayDate: formDate,
        description: formDescription.trim() || null,
        actionById: userId,
        actionByName: userName || 'Admin'
      };

      if (isEditing) {
        payload.id = editingId;
        await holidayServices.updateHoliday(payload, userId, userName, userRoles);
        showSnackbar('Holiday updated successfully!', 'success');
      } else {
        await holidayServices.addHoliday(payload, userId, userName, userRoles);
        showSnackbar('Holiday added successfully!', 'success');
      }

      setOpenDialog(false);
      fetchHolidays();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to save holiday.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!holidayToDelete) return;
    try {
      setDeleting(true);
      await holidayServices.deleteHoliday(holidayToDelete.id, userId, userRoles);
      showSnackbar('Holiday deleted successfully!', 'success');
      setDeleteDialogOpen(false);
      setHolidayToDelete(null);
      fetchHolidays();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to delete holiday.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Table Columns (Date, Holiday Name, Created By, Action)
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Holiday Name',
      flex: 2.5,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span style={{ fontWeight: 600 }}>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'holidayDate',
      headerName: 'Date',
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <Tooltip title={toDDMMYYYY(params.value)}>
          <span style={{ fontWeight: 500 }}>{toDDMMYYYY(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'createdByName',
      headerName: 'Created By',
      flex: 2,
      minWidth: 160,
      renderCell: (params) => (
        <Tooltip title={params.value || '—'}>
          <span>{params.value || '—'}</span>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 120,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title="Edit Holiday">
            <IconButton
              color="primary"
              size="small"
              className="icon-button"
              onClick={(event) => handleOpenEdit(event, params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Holiday">
            <IconButton
              color="error"
              size="small"
              className="icon-button"
              onClick={(event) => handleOpenDelete(event, params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ], []);

  const todayISO = getTodayISO();

  return (
    <div className="data-table-container">
      {/* Header */}
      <PageHeader title="Holidays" />

      {/* Snackbar */}
      {SnackbarComponent}

      {/* Action and Search Bar */}
      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleOpenAdd}
          className="add-button"
          startIcon={<AddIcon />}
        >
          Add
        </Button>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search Holidays..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={4} />
            </Box>
          ) : (
            <DataGrid
              rows={holidays}
              columns={columns}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          )}
        </div>
      </div>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        disableRestoreFocus
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="modern-dialog-title">
          <span className="modern-dialog-title-text">
            {isEditing ? 'Edit Holiday' : 'Add Holiday'}
          </span>
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            disabled={submitting}
            className="modern-dialog-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modern-dialog-content">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
            <TextField
              label="Holiday Name"
              required
              fullWidth
              size="small"
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value);
                if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: null }));
              }}
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              placeholder="e.g. Republic Day, Diwali"
            />

            <TextField
              label="Holiday Date"
              type="date"
              required
              fullWidth
              size="small"
              value={formDate}
              onChange={(e) => {
                setFormDate(e.target.value);
                if (formErrors.date) setFormErrors((prev) => ({ ...prev, date: null }));
              }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: todayISO },
              }}
              error={Boolean(formErrors.date)}
              helperText={formErrors.date}
            />

            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              size="small"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              slotProps={{
                htmlInput: { maxLength: 500 },
              }}
              placeholder="Optional description or details..."
            />
          </div>
        </DialogContent>

        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            color="inherit"
            disabled={submitting}
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveHoliday}
            variant="contained"
            className="add-button"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (Reusing standardized ConfirmationDialog) */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Holiday"
        message={`Are you sure you want to delete the holiday "${holidayToDelete?.name}" on ${toDDMMYYYY(holidayToDelete?.holidayDate)}?`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        isDelete={true}
      />
    </div>
  );
};

export default HolidayManagement;
